import "./App.css"
import { Uploader } from "./utils/upload"
import { useState, useRef, useCallback, useEffect } from "react"

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || ""
const MAX_RETRIES = 3

function formatBytes(bytes) {
  if (bytes === 0) return "0 B"
  const k = 1024
  const sizes = ["B", "KB", "MB", "GB", "TB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i]
}

function formatSpeed(bytesPerSec) {
  if (!bytesPerSec || bytesPerSec <= 0) return ""
  return formatBytes(bytesPerSec) + "/s"
}

let fileIdCounter = 0

function App() {
  const [queue, setQueue] = useState([])
  const [dragging, setDragging] = useState(false)
  const [uploadStartTime, setUploadStartTime] = useState(null)
  const inputRef = useRef(null)
  const processingRef = useRef(false)
  const queueRef = useRef([])

  queueRef.current = queue

  const updateFile = useCallback((id, updates) => {
    setQueue(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f))
  }, [])

  const addFiles = useCallback((fileList) => {
    const newFiles = Array.from(fileList).map(file => ({
      id: ++fileIdCounter,
      file,
      name: file.name,
      size: file.size,
      status: "waiting",
      progress: 0,
      retries: 0,
    }))
    setQueue(prev => [...prev, ...newFiles])
    if (!uploadStartTime) {
      setUploadStartTime(Date.now())
    }
  }, [])

  const uploadFile = useCallback(async (item) => {
    const startTime = Date.now()
    return new Promise((resolve) => {
      const uploader = new Uploader({
        file: item.file,
        baseURL: BACKEND_URL,
        chunkSize: 10,
        threadsQuantity: 5,
        compressData: false,
      })

      uploader
        .onProgress(({ percentage, sent }) => {
          const elapsed = (Date.now() - startTime) / 1000
          const speed = elapsed > 0 ? (sent / elapsed) : 0
          updateFile(item.id, { progress: percentage, speed })
          if (percentage >= 100) {
            updateFile(item.id, { status: "done", progress: 100, speed: 0 })
            resolve(true)
          }
        })
        .onError((error) => {
          console.error(`Upload error for ${item.name}:`, error)
          resolve(false)
        })

      uploader.start()
    })
  }, [updateFile])

  const processQueue = useCallback(async () => {
    if (processingRef.current) return
    processingRef.current = true

    while (true) {
      const current = queueRef.current.find(f => f.status === "waiting" || f.status === "retrying")
      if (!current) break

      updateFile(current.id, { status: "uploading", progress: 0 })
      const success = await uploadFile(current)

      if (!success) {
        const latest = queueRef.current.find(f => f.id === current.id)
        if (latest && latest.retries < MAX_RETRIES) {
          const delay = Math.pow(2, latest.retries) * 2000
          updateFile(current.id, {
            status: "retrying",
            progress: 0,
            retries: latest.retries + 1,
          })
          await new Promise(r => setTimeout(r, delay))
        } else {
          updateFile(current.id, { status: "failed" })
        }
      }
    }

    processingRef.current = false
  }, [uploadFile, updateFile])

  useEffect(() => {
    const hasWaiting = queue.some(f => f.status === "waiting" || f.status === "retrying")
    if (hasWaiting && !processingRef.current) {
      processQueue()
    }
  }, [queue, processQueue])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragging(false)
    addFiles(e.dataTransfer.files)
  }, [addFiles])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    setDragging(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    setDragging(false)
  }, [])

  const completedCount = queue.filter(f => f.status === "done").length
  const failedCount = queue.filter(f => f.status === "failed").length
  const totalSize = queue.reduce((sum, f) => sum + f.size, 0)
  const doneSize = queue.filter(f => f.status === "done").reduce((sum, f) => sum + f.size, 0)
  const overallProgress = totalSize > 0 ? Math.round((doneSize / totalSize) * 100) : 0
  const isProcessing = queue.some(f => f.status === "uploading" || f.status === "waiting" || f.status === "retrying")

  const overallSpeed = (() => {
    if (!uploadStartTime || !isProcessing || doneSize === 0) return 0
    const elapsed = (Date.now() - uploadStartTime) / 1000
    return elapsed > 0 ? doneSize / elapsed : 0
  })()

  return (
    <div className="app">
      <h1>Mansfield MO Pride Uploads</h1>
      <p className="subtitle">Share your files securely</p>

      <div
        className={`dropzone ${dragging ? "active" : ""}`}
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className="dropzone-icon">+</div>
        <div className="dropzone-text">
          <strong>Click to choose files</strong> or drag and drop
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            addFiles(e.target.files)
            e.target.value = ""
          }}
        />
      </div>

      {queue.length > 0 && (
        <>
          <div className="queue-stats">
            <span>{completedCount}/{queue.length} uploaded</span>
            <span>{formatBytes(doneSize)} / {formatBytes(totalSize)}</span>
          </div>

          <div className="progress-container">
            <div className="progress-bar-bg">
              <div
                className="progress-bar-fill"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
            <div className="progress-text">
              <span>{overallProgress}%</span>
              <span>{isProcessing && overallSpeed > 0 ? formatSpeed(overallSpeed) : (completedCount === queue.length && queue.length > 0 ? "All done!" : "")}</span>
            </div>
          </div>

          {failedCount > 0 && (
            <div className="status-msg status-error">
              {failedCount} file(s) failed to upload after {MAX_RETRIES} retries
            </div>
          )}

          <div className="file-list">
            {queue.map((item) => (
              <div key={item.id} className={`file-item file-item-${item.status}`}>
                <div className="file-item-info">
                  <span className="file-name">{item.name}</span>
                  <span className="file-size">{formatBytes(item.size)}</span>
                </div>
                <div className="file-item-status">
                  {item.status === "waiting" && "Waiting..."}
                  {item.status === "retrying" && `Retrying (${item.retries}/${MAX_RETRIES})...`}
                  {item.status === "uploading" && (
                    <div className="file-item-progress">
                      <div className="file-item-progress-bar">
                        <div className="file-item-progress-fill" style={{ width: `${item.progress}%` }} />
                      </div>
                      <span>{item.progress}%</span>
                    </div>
                  )}
                  {item.status === "done" && "Done"}
                  {item.status === "failed" && "Failed"}
                </div>
              </div>
            ))}
          </div>

          {completedCount === queue.length && queue.length > 0 && (
            <div className="status-msg status-success">
              All uploads complete!
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default App
